import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionTitle } from '@/components/common/SectionTitle';
import { CourseCard } from '@/components/common/CourseCard';
import { Card, CardContent } from '@/components/ui/card';
import { BookOpen } from 'lucide-react';
import { fetchCourses } from '@/store/slices/coursesSlice';

const CoursesPage = () => {
  const [activeClass, setActiveClass] = useState('ALL');

  const dispatch = useDispatch();
  const { data, fetchLoading, error } = useSelector((state) => state.courses);

  useEffect(() => {
    dispatch(fetchCourses());
  }, [dispatch]);

  const apiCourses = data?.data || [];

  /* API SHAPE → UI SHAPE */
  const coursesData = apiCourses.map((c) => ({
    id: c.id,
    name: c.name,
    class: c.class_name,
    target: c.category,
    description: c.description,
    duration: c.duration,
    features: Array.isArray(c.exam_preparation)
      ? c.exam_preparation
      : (c.exam_preparation || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
    icon: <BookOpen className="h-12 w-12 text-primary m-auto" />
  }));

  /* DYNAMIC CLASS TABS — backend se aayi hui unique class_name values se banta hai */
  const uniqueClasses = [...new Set(coursesData.map((c) => c.class).filter(Boolean))];

  const classTabs = [
    { label: 'All', value: 'ALL' },
    ...uniqueClasses.map((cls) => ({ label: cls, value: cls }))
  ];

  const filteredCourses =
    activeClass === 'ALL'
      ? coursesData
      : coursesData.filter((course) => course.class === activeClass);

  return (
    <div className="bg-background">
      <PageHeader
        title="Our Courses"
        subtitle="Structured programs designed class-wise for academic and competitive excellence"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Courses' }
        ]}
      />

      <section className="section-padding">
        <div className="container-custom">
          <Card className="mb-14 sticky top-20 z-20 bg-background/95 backdrop-blur border shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2 justify-center relative">
                {classTabs.map((tab) => {
                  const isActive = activeClass === tab.value;
                  return (
                    <button
                      key={tab.value}
                      onClick={() => setActiveClass(tab.value)}
                      className={`relative lg:px-6 px-4 lg:py-2.5 py-2 rounded-full text-sm font-semibold transition-colors duration-300
          ${isActive ? 'text-white' : 'text-muted-foreground hover:text-primary'}`}
                    >
                      {isActive && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute inset-0 bg-primary rounded-full -z-10 shadow-lg shadow-primary/20"
                          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                        />
                      )}
                      <span className="relative z-10">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <SectionTitle
            title={
              activeClass === 'ALL'
                ? 'All Programs'
                : `Programs for ${activeClass}`
            }
            description="Choose the right program based on your current class and academic goals"
            className="mb-16"
          />

          {filteredCourses.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">
                No programs available for this class.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCourses.map((course, index) => (
                <CourseCard key={course.id} course={course} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CoursesPage;